import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, isDirectorUser } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { createAuthFor } from '../../api';
import { Icon, InlineIcon } from '../../components/Icon';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { useTableTools } from '../../core/tableView';
import { SortableTh } from '../../components/SortableTh';

const DIRECTOR_ACCESSORS = {
  name: (d) => (d.name || '').toLowerCase(),
  login: (d) => (d.email || d.phone || '').toLowerCase(),
  designation: (d) => (d.designation || '').toLowerCase(),
  din: (d) => d.din || '',
  pan: (d) => d.pan || '',
  dsc: (d) => d.dsc_expiry || '',
};


function DirectorsPage() {
  const { clients, users, userLinks, setUserLinks, setAllUsers, showToast } = useApp();
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name:"", email:"", phone:"", password:"", din:"", pan:"", dscExpiry:"", designation:"director", companyIds:[] });
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);       // #G8 inline-edit a director's statutory profile
  const [editForm, setEditForm] = useState({});

  const directors = (users||[]).filter(u => isDirectorUser(u, clients));
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const table = useTableTools(directors, {
    search,
    searchFn: (d, q) => {
      const s = q.toLowerCase();
      return (d.name || '').toLowerCase().includes(s)
        || (d.email || '').toLowerCase().includes(s)
        || (d.phone || '').includes(s)
        || (d.din || '').toLowerCase().includes(s)
        || (d.pan || '').toLowerCase().includes(s);
    },
    accessors: DIRECTOR_ACCESSORS,
    sortKey,
    sortDir,
    onSort: toggleSort,
    defaultSort: { key: 'name', dir: 'asc' },
  });
  const linkedIds = (uid) => userLinks.filter(l => l.user_id === uid).map(l => l.company_id);
  const compName  = (id) => clients.find(c=>c.id===id)?.name || "Unknown company";
  const toggleCo  = (id) => setForm(f => ({ ...f, companyIds: f.companyIds.includes(id) ? f.companyIds.filter(x=>x!==id) : [...f.companyIds, id] }));

  const save = async () => {
    if (!form.name || (!form.email && !form.phone) || !form.password) {
      showToast("Name, an email or phone, and a password are required", "error"); return;
    }
    if (form.password.length < 6) { showToast("Password must be at least 6 characters", "error"); return; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { showToast("Enter a valid email address", "error"); return; }
    if (form.pan && !/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(form.pan.trim())) { showToast("PAN looks wrong — format AABCF1234A", "error"); return; }
    setBusy(true);
    const id = crypto.randomUUID();
    const profile = {
      din: form.din || null,
      pan: form.pan ? form.pan.toUpperCase() : null,
      dsc_expiry: form.dscExpiry || null,
      designation: form.designation || "director",
    };
    const r = await sb.createUser({
      id,
      email: form.email ? form.email.toLowerCase() : null,
      phone: form.phone || null,
      name: form.name,
      role: "client",
      avatar: initials(form.name),
      color: "#7C3AED",
      company_id: form.companyIds[0] || null,
      ...profile,
    });
    if (!r || r.length === 0) { showToast("Failed to create director — check console", "error"); setBusy(false); return; }
    const authRes = await createAuthFor({ email: form.email, phone: form.phone, password: form.password, appUserId: id, name: form.name, role: "client" });
    if (!authRes.ok) showToast("Director saved, but login couldn't be created: " + (authRes.error || "use Reset Password to set it") + ".", "warning");
    const newLinks = [];
    for (const cid of form.companyIds) {
      try {
        await sb.insert("user_companies", { user_id: id, company_id: cid });
        newLinks.push({ user_id: id, company_id: cid });
      } catch { /* ignore duplicate */ }
    }
    const created = {
      id,
      email: form.email ? form.email.toLowerCase() : null,
      phone: form.phone || null,
      name: form.name,
      role: "client",
      avatar: initials(form.name),
      color: "#7C3AED",
      permissions: {},
      ...profile,
    };
    setAllUsers(us => [...us, created]);
    if (newLinks.length) setUserLinks(ls => [...ls, ...newLinks]);
    showToast(`Director ${form.name} created — linked to ${form.companyIds.length} compan${form.companyIds.length === 1 ? "y" : "ies"}`, "success");
    setShowNew(false);
    setForm({ name: "", email: "", phone: "", password: "", din: "", pan: "", dscExpiry: "", designation: "director", companyIds: [] });
    setBusy(false);
  };

  // #G8 Save statutory profile edits (DIN / PAN / DSC expiry / designation) for an existing director
  const startEdit = (d) => { setEditId(d.id); setEditForm({ din:d.din||"", pan:d.pan||"", dscExpiry:d.dsc_expiry||"", designation:d.designation||"director" }); };
  const saveEdit  = async (id) => {
    if (editForm.pan && !/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(editForm.pan.trim())) { showToast("PAN looks wrong — format AABCF1234A", "error"); return; }
    const patch = {
      din: editForm.din || null,
      pan: editForm.pan ? editForm.pan.toUpperCase() : null,
      dsc_expiry: editForm.dscExpiry || null,
      designation: editForm.designation || "director",
    };
    await sb.updateUser(id, patch);
    setAllUsers(us => us.map(u => u.id === id ? { ...u, ...patch } : u));
    showToast("Director profile updated", "success");
    setEditId(null);
  };
  const dscState = (exp) => {
    if (!exp) return null;
    const days = Math.ceil((new Date(exp) - new Date()) / 86400000);
    if (days < 0)  return { txt:`Expired ${Math.abs(days)}d ago`, color:"var(--red)" };
    if (days <= 30) return { txt:`Expires in ${days}d`, color:"var(--orange)" };
    return { txt:new Date(exp).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}), color:"var(--muted)" };
  };

  const addLink = async (uid, cid) => {
    if (!cid) return;
    try { await sb.insert("user_companies", { user_id: uid, company_id: cid }); } catch {}
    setUserLinks(ls => [...ls, { user_id: uid, company_id: cid }]);
  };
  const removeLink = async (uid, cid) => {
    try {
      await sb.delete("user_companies", `user_id=eq.${uid}&company_id=eq.${cid}`);
    } catch {}
    setUserLinks(ls => ls.filter(l => !(l.user_id===uid && l.company_id===cid)));
  };

  return (
    <>
      <div style={{ display:"flex", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontSize:13, color:"var(--muted)", flex:1 }}>
          A director is one login that can see multiple companies. Create the person, then tick the companies they own.
        </div>
        <button className="btn btn-primary" onClick={()=>setShowNew(s=>!s)}>{showNew ? "Cancel" : "+ New Director"}</button>
      </div>

      {showNew && (
        <div className="card card-gold" style={{ marginBottom:20 }}>
          <div className="card-head"><div className="card-title">New Director Login</div></div>
          <div className="card-body">
            <div className="form-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div><label className="f-lbl">Full name *</label><input className="f-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Ramesh Gupta" /></div>
              <div><label className="f-lbl">Email</label><input className="f-input" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="director@email.com" /></div>
              <div><label className="f-lbl">Phone</label><input className="f-input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="10-digit mobile" /></div>
              <div><label className="f-lbl">Password *</label><input className="f-input" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="Temporary login password" /></div>
              <div><label className="f-lbl">DIN</label><input className="f-input" value={form.din} onChange={e=>setForm(f=>({...f,din:e.target.value}))} placeholder="8-digit Director ID No." /></div>
              <div><label className="f-lbl">PAN</label><input className="f-input" value={form.pan} onChange={e=>setForm(f=>({...f,pan:e.target.value}))} placeholder="AABCF1234A" /></div>
              <div><label className="f-lbl">DSC Expiry</label><input className="f-input" type="date" value={form.dscExpiry} onChange={e=>setForm(f=>({...f,dscExpiry:e.target.value}))} /></div>
              <div><label className="f-lbl">Designation</label>
                <select className="f-input" value={form.designation} onChange={e=>setForm(f=>({...f,designation:e.target.value}))}>
                  <option value="director">Director</option>
                  <option value="authorised_signatory">Authorised Signatory</option>
                  <option value="shareholder">Shareholder</option>
                  <option value="contact">Contact Person</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop:14 }}>
              <label className="f-lbl">Companies this director can access</label>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:8, marginTop:6, maxHeight:220, overflow:"auto", padding:4 }}>
                {clients.length === 0 && <div style={{ fontSize:12, color:"var(--muted)" }}>No companies yet — create clients first.</div>}
                {clients.map(c => (
                  <label key={c.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", border:"1px solid var(--border)", borderRadius:7, cursor:"pointer", background: form.companyIds.includes(c.id) ? "#EEF2FF" : "#fff" }}>
                    <input type="checkbox" checked={form.companyIds.includes(c.id)} onChange={()=>toggleCo(c.id)} />
                    <span style={{ fontSize:13 }}>{c.name} <span style={{ color:"var(--muted)", fontSize:11 }}>({c.clientNo})</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ marginTop:16, display:"flex", gap:10 }}>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Create Director"}</button>
              <button className="btn" onClick={()=>setShowNew(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <FilterBar
        search={search} setSearch={setSearch} searchPlaceholder="Search director / DIN / PAN…"
        onClear={() => setSearch("")}
      />

      <div className="card">
        <div className="card-head"><div className="card-title">Directors ({table.total})</div></div>
        {directors.length === 0 ? (
          <div className="empty"><div className="empty-icon"><Icon name="directors" size={30} /></div><div>No directors yet. Create one to give a person access to several companies.</div></div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead><tr>
                <SortableTh label="Name" sortKey="name" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Login" sortKey="login" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Designation" sortKey="designation" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="DIN" sortKey="din" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="PAN" sortKey="pan" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="DSC Expiry" sortKey="dsc" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Linked companies</th><th></th>
              </tr></thead>
              <tbody>
                {table.visible.map(d => {
                  const mine = linkedIds(d.id);
                  const addable = clients.filter(c => !mine.includes(c.id));
                  return (
                    <tr key={d.id}>
                      <td style={{ fontWeight:700, color:"var(--navy)" }}>{d.name}</td>
                      <td style={{ fontSize:12, color:"var(--muted)" }}>{d.email || d.phone || "—"}</td>
                      {editId === d.id ? (
                        <>
                          <td><select className="f-input" style={{ minWidth:120, fontSize:12 }} value={editForm.designation} onChange={e=>setEditForm(f=>({...f,designation:e.target.value}))}>
                            <option value="director">Director</option><option value="authorised_signatory">Auth. Signatory</option><option value="shareholder">Shareholder</option><option value="contact">Contact</option>
                          </select></td>
                          <td><input className="f-input" style={{ width:100, fontSize:12 }} value={editForm.din} onChange={e=>setEditForm(f=>({...f,din:e.target.value}))} placeholder="DIN" /></td>
                          <td><input className="f-input" style={{ width:110, fontSize:12 }} value={editForm.pan} onChange={e=>setEditForm(f=>({...f,pan:e.target.value}))} placeholder="PAN" /></td>
                          <td><input className="f-input" type="date" style={{ width:140, fontSize:12 }} value={editForm.dscExpiry} onChange={e=>setEditForm(f=>({...f,dscExpiry:e.target.value}))} /></td>
                        </>
                      ) : (
                        <>
                          <td style={{ fontSize:12, textTransform:"capitalize" }}>{(d.designation||"director").replace(/_/g," ")}</td>
                          <td style={{ fontSize:12 }}>{d.din || "—"}</td>
                          <td style={{ fontSize:12 }}>{d.pan || "—"}</td>
                          <td style={{ fontSize:12 }}>{(() => { const s=dscState(d.dsc_expiry); return s ? <span style={{ color:s.color, fontWeight:600 }}>{s.txt}</span> : "—"; })()}</td>
                        </>
                      )}
                      <td>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                          {mine.length === 0 && <span style={{ fontSize:12, color:"var(--muted)" }}>None</span>}
                          {mine.map(cid => (
                            <span key={cid} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#EEF2FF", border:"1px solid #C7D2FE", borderRadius:20, padding:"3px 10px", fontSize:12 }}>
                              {compName(cid)}
                              <span style={{ cursor:"pointer", color:"#B91C1C", fontWeight:700 }} title="Remove" onClick={()=>removeLink(d.id, cid)}>×</span>
                            </span>
                          ))}
                        </div>
                        <select className="f-input" style={{ minWidth:150, marginTop:6, fontSize:12 }} value="" onChange={e=>{ addLink(d.id, e.target.value); e.target.value=""; }}>
                          <option value="">+ Add company…</option>
                          {addable.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </td>
                      <td style={{ whiteSpace:"nowrap" }}>
                        {editId === d.id
                          ? <><button className="btn btn-sm btn-primary" onClick={()=>saveEdit(d.id)}>Save</button> <button className="btn btn-sm" onClick={()=>setEditId(null)}><Icon name="close" size={13} /></button></>
                          : <button className="btn btn-sm" onClick={()=>startEdit(d)}><InlineIcon name="edit" size={13} gap={4}>Edit</InlineIcon></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <LoadMoreBar hasMore={table.hasMore} loadingMore={false} onLoadMore={table.loadMore} count={table.shownCount} />
          </div>
        )}
      </div>
    </>
  );
}


export { DirectorsPage };
